/**
 * Resilient edge function invocation wrapper.
 * Provides exponential backoff, timeout handling, and a consistent error shape
 * to replace ad-hoc try/catch blocks across the codebase.
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/structuredLogging';

export interface EdgeFunctionError extends Error {
  functionName: string;
  statusCode?: number;
  isRetryable: boolean;
  attempts: number;
}

export interface InvokeOptions {
  /** Request body */
  body?: Record<string, unknown>;
  /** Timeout in ms (default 30000) */
  timeout?: number;
  /** Max retry attempts (default 2, so up to 3 total calls) */
  maxRetries?: number;
  /** Initial backoff delay in ms (default 1000) */
  initialDelay?: number;
  /** Headers to forward */
  headers?: Record<string, string>;
}

const DEFAULT_TIMEOUT = 30_000;
const DEFAULT_MAX_RETRIES = 2;
const DEFAULT_INITIAL_DELAY = 1_000;
const MAX_DELAY = 10_000;

/**
 * Determine whether an error is retryable.
 * Retries on: network errors, timeouts, 5xx, 429 (rate limit).
 */
function isRetryable(error: unknown): boolean {
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    if (msg.includes('timeout') || msg.includes('network') || msg.includes('fetch')) {
      return true;
    }
  }
  // Supabase FunctionsHttpError includes status in the message
  const errStr = String(error);
  if (/5\d{2}|429|rate.?limit|gateway|timed?\s*out/i.test(errStr)) {
    return true;
  }
  return false;
}

interface FunctionErrorDetails {
  message: string;
  statusCode?: number;
  retryAfter?: number;
}

/**
 * Supabase exposes a failed function's Response on `error.context`. Read that
 * response when available so callers can distinguish an expected 429 from an
 * opaque transport failure.
 */
async function getFunctionErrorDetails(error: unknown): Promise<FunctionErrorDetails> {
  const fallback = error instanceof Error ? error.message : String(error);
  if (typeof error !== 'object' || error === null || !('context' in error)) {
    return { message: fallback };
  }

  const context = (error as { context?: unknown }).context;
  if (!(context instanceof Response)) return { message: fallback };

  const statusCode = context.status;
  const headerDelay = Number(context.headers.get('Retry-After'));
  let body: { error?: unknown; message?: unknown; retryAfter?: unknown } | null = null;
  try {
    body = await context.clone().json();
  } catch {
    // An empty/non-JSON error response still carries a useful status code.
  }

  const bodyMessage = typeof body?.message === 'string'
    ? body.message
    : typeof body?.error === 'string'
      ? body.error
      : fallback;
  const bodyDelay = Number(body?.retryAfter);
  const retryAfter = Number.isFinite(bodyDelay)
    ? bodyDelay
    : Number.isFinite(headerDelay)
      ? headerDelay
      : undefined;

  return { message: bodyMessage, statusCode, retryAfter };
}

/**
 * Invoke a Supabase edge function with retry + timeout.
 *
 * @example
 * const result = await invokeEdgeFunction<{ plan: object }>('integral-evolution', {
 *   body: { stage: 'yellow', analysisResult },
 *   timeout: 60_000,
 * });
 */
export async function invokeEdgeFunction<T = unknown>(
  functionName: string,
  options: InvokeOptions = {}
): Promise<T> {
  const {
    body,
    timeout = DEFAULT_TIMEOUT,
    maxRetries = DEFAULT_MAX_RETRIES,
    initialDelay = DEFAULT_INITIAL_DELAY,
    headers,
  } = options;

  let lastError: unknown;
  let lastDetails: FunctionErrorDetails | undefined;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeout);

      const { data, error } = await supabase.functions.invoke(functionName, {
        body,
        headers: {
          ...headers,
        },
      });

      clearTimeout(timer);

      if (error) {
        throw error;
      }

      return data as T;
    } catch (error) {
      lastError = error;

      const details = await getFunctionErrorDetails(error);
      lastDetails = details;

      const shouldRetry = attempt < maxRetries && isRetryable(error);

      logger.warn(`Edge function "${functionName}" attempt ${attempt + 1} failed`, {
        component: 'edgeFunctionRetry',
        action: 'invoke',
        metadata: {
          functionName,
          attempt: attempt + 1,
          willRetry: shouldRetry,
          error: details.message,
          statusCode: details.statusCode,
        },
      });

      if (!shouldRetry) break;

      const delay = Math.min(initialDelay * Math.pow(2, attempt), MAX_DELAY) + Math.random() * 500;
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // Build a structured error
  const edgeError: EdgeFunctionError = Object.assign(
    new Error(
      lastDetails?.message
        ? lastDetails.message
        : lastError instanceof Error
        ? lastError.message
        : `Edge function "${functionName}" failed after ${maxRetries + 1} attempts`
    ),
    {
      name: 'EdgeFunctionError',
      functionName,
      statusCode: lastDetails?.statusCode,
      retryAfter: lastDetails?.retryAfter,
      isRetryable: isRetryable(lastError),
      attempts: maxRetries + 1,
    }
  );

  logger.error(`Edge function "${functionName}" exhausted retries`, {
    component: 'edgeFunctionRetry',
    action: 'invoke',
    metadata: { functionName, attempts: maxRetries + 1 },
  }, edgeError);

  throw edgeError;
}
